'use client';
import { getValidatedQueryParameters } from '@svenskaspelab/poolbetting/core/lib/client';
import type { UploadErrorType } from '@svenskaspelab/poolbetting/schema/input';
import { Button } from '@svenskaspelab/ui/react/button';
import { useSearchParams } from 'next/navigation';
import { type ReactNode /* , useActionState */ } from 'react';

const uploadErrorMessages = {
  UNAUTHORIZED: 'Du har inte behörighet att ladda upp en fil.',
  CLIENT: 'Försök igen senare.',
  CONTENT_INVALID: 'Filen du valde är inte korrekt.',
  CONTENT_INVALID_HEADER: 'Det gick inte att läsa ut data från filen. Kontrollera att den är korrekt formaterad.',
  CONTENT_BET_AMOUNT_MISSING: 'Den angivna insatsen är inte tillåten.',
  CONTENT_DRAW_NUMBER_MISSING: 'Omgångsnummer saknas eller är inte giltigt i filen.',
  CONTENT_SYSTEM_SIZE_MISSING: 'Filen innehåller ett eller flera för stora M-system.',
  CONTENT_SYSTEM_UNSUPPORTED: 'Systemtypen i filen stöds inte för Powerplay.',
  CONTENT_SYSTEM_MISSING: 'Systemet du skickade in är inte korrekt.',
  CONTENT_SYSTEM_INCONSISTENT: 'Systemet du skickade in är inte korrekt.',
  CONTENT_SYSTEM_SIZE_UNEXPECTED: 'Filen innehåller ett eller flera för stora M-system.',
  DRAW_CLOSED: 'Omgångsnumret finns inte eller så finns det ingen spelbar omgång på det angivna spelet.',
  PRODUCT_NOT_SUPPORTED: 'Produkten i filen stöds inte.',
  PRODUCT_MISMATCH:
    'Produktnamnet verkar inte stämma. Dubbelkolla att första ordet är detsamma för den produkt du är inne på just nu.',
  AD_DRAW_MISMATCH: 'Omgångsnumret finns inte eller så finns det ingen spelbar omgång på det angivna spelet.',
  INTERNAL: 'Försök igen senare.',
  UNKNOWN: 'Försök igen senare.',
} satisfies Record<UploadErrorType, string>;

/**
 * Renders an file upload form.
 *
 * @returns file upload form component
 */
export function FileUpload(): ReactNode {
  const inputId = 'external-systems-file';

  const search = useSearchParams();
  const { activatedDrawId, error } = getValidatedQueryParameters('activatedDrawId', 'error')(search);

  const parameters = [];
  activatedDrawId && parameters.push(['activatedDrawId', activatedDrawId.toString()]);
  const qs = new URLSearchParams(parameters);

  const errorMessage = error ? uploadErrorMessages[error] : undefined;

  return (
    <form
      className="flex flex-col items-center"
      encType="multipart/form-data"
      action={`/powerplay-api/upload?${qs}`}
      method="POST"
    >
      <p className="mt-3 mb-3">Ladda upp ett spel genom en textfil</p>

      {errorMessage ? (
        <p
          aria-live="polite"
          className="mb-3 w-full rounded border border-red-300 bg-red-50 px-4 py-3 text-left text-sm text-red-800"
          role="alert"
        >
          {errorMessage}
        </p>
      ) : undefined}

      {/* The visible text is supplied by Base UI from Button's children. */}
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <Button render={<label htmlFor={inputId} />}>Ladda upp fil</Button>

      <input
        accept="text/plain"
        className="sr-only"
        id={inputId}
        name="file"
        type="file"
        onChange={(event) => {
          if (event.currentTarget.files?.length) {
            event.currentTarget.form?.requestSubmit();
          }
        }}
      />
    </form>
  );
}
